import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { timeAgo, type InAppNotification } from '@sd/lib/inAppNotifications';
import { getNotificationKindMeta } from '@sd/lib/notificationKindMeta';

type NotificationListItemProps = {
  item: InAppNotification;
  isMarking?: boolean;
  onMarkRead?: (id: string) => void;
  /** Ticket link target — admin uses admin/tickets, agent uses tickets */
  ticketLinkTo?: string;
  className?: string;
};

export function NotificationListItem({
  item,
  isMarking = false,
  onMarkRead,
  ticketLinkTo,
  className,
}: NotificationListItemProps) {
  const meta = getNotificationKindMeta(item.kind);
  const Icon = meta.icon;
  const isUnread = !item.read;

  const handleActivate = () => {
    if (isUnread && !isMarking && onMarkRead) onMarkRead(item.id);
  };

  return (
    <div
      role={isUnread && onMarkRead ? 'button' : undefined}
      tabIndex={isUnread && onMarkRead ? 0 : undefined}
      onClick={handleActivate}
      onKeyDown={e => {
        if (isUnread && onMarkRead && (e.key === 'Enter' || e.key === ' ') && !isMarking) {
          e.preventDefault();
          onMarkRead(item.id);
        }
      }}
      className={cn(
        'relative rounded-lg border border-l-[3px] px-4 py-4 shadow-sm transition-colors',
        isUnread && onMarkRead
          ? 'cursor-pointer border-l-blue-600 bg-slate-200/90 hover:bg-slate-300/80 dark:border-l-blue-400 dark:bg-slate-800 dark:hover:bg-slate-700'
          : isUnread
            ? 'border-l-blue-600 bg-slate-200/90 dark:border-l-blue-400 dark:bg-slate-800'
            : 'border-l-muted-foreground/25 bg-card opacity-90 dark:bg-slate-900/50',
        className
      )}
    >
      {isUnread && (
        <span
          className="absolute right-4 top-4 h-2.5 w-2.5 rounded-full bg-primary shadow-sm"
          title="Unread"
        />
      )}

      <div className="flex gap-3 pr-6">
        <div
          className={cn(
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
            isUnread ? 'bg-slate-300/80 dark:bg-slate-700' : 'bg-muted'
          )}
        >
          <Icon className={cn('h-[18px] w-[18px]', isUnread ? meta.iconClass : 'text-muted-foreground')} />
        </div>
        <div className="min-w-0 flex-1 space-y-1.5">
          <h2
            className={cn(
              'text-base leading-snug',
              isUnread
                ? 'font-semibold text-slate-900 dark:text-slate-50'
                : 'font-medium text-foreground/80'
            )}
          >
            {(() => {
              const subject = item.subject;
              const id = item.ticketId !== '—' ? item.ticketId : null;
              return (
                <span className="flex items-center gap-2 flex-wrap">
                  {id && ticketLinkTo && (
                    <Link
                      to={ticketLinkTo}
                      onClick={e => e.stopPropagation()}
                      className={cn(
                        'font-bold text-base tracking-wide hover:underline',
                        isUnread ? 'text-blue-700 dark:text-blue-400' : 'text-blue-600/70'
                      )}
                    >
                      {id}
                    </Link>
                  )}
                  {id && !ticketLinkTo && (
                    <span className={cn('font-bold text-base tracking-wide font-mono', isUnread ? 'text-blue-700 dark:text-blue-400' : 'text-blue-600/70')}>
                      {id}
                    </span>
                  )}
                  {subject && (
                    <span className={cn(
                      'font-semibold text-sm',
                      isUnread ? 'text-slate-800 dark:text-slate-200' : 'text-foreground/70'
                    )}>
                      · {subject}
                    </span>
                  )}
                  {!id && !subject && item.title}
                </span>
              );
            })()}
          </h2>
          <p
            className={cn(
              'text-sm leading-relaxed',
              isUnread ? 'text-slate-700 dark:text-slate-300' : 'text-muted-foreground'
            )}
          >
            {item.body.replace(/^New task assigned in Service Desk:\s*TXT-\d+\s*[-–]\s*[""].*?[""]\.\s*/i, '')}
          </p>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 pt-0.5 text-xs text-muted-foreground">
            <span>{timeAgo(item.createdAt)}</span>
            <span className="text-border">·</span>
            <Badge
              variant="secondary"
              className="h-5 rounded px-2 text-[11px] font-normal text-primary bg-primary/10 hover:bg-primary/10"
            >
              {meta.label}
            </Badge>
          </div>
        </div>
      </div>

      {isMarking && (
        <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-background/60">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
        </div>
      )}
    </div>
  );
}
